<template>
  <div v-if="loaded">
    <v-row>
      <v-col>
        <v-btn
          @click="updateQuery({ mode: 'login' })"
          :ripple="false"
          class="focus-visible fs-16 fw-500 c-primary bc-link-hover px-0 mt-0 underlined"
          data-cy="login-mode-button"
          data-testid="login-mode-button"
          tabindex="0"
          variant="text"
        >
          {{ $bct('Log in if you already have an account.') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row dense>
      <LabeledTextfield
        v-model="firstName"
        :message="'First name'"
        aria-label="Enter first name"
        class="mb-2"
        cols="12"
        data-cy="input_first_name"
        data-testid="input_first_name"
        md="6"
        mandatory
        outlined
      />

      <LabeledTextfield
        v-model="lastName"
        :message="'Last name'"
        aria-label="Enter last name"
        class="mb-2"
        cols="12"
        data-cy="input_last_name"
        data-testid="input_last_name"
        md="6"
        mandatory
        outlined
      />
    </v-row>
    <v-row dense>
      <template v-if="$store.state.config.enable_phone_registration">
        <LabeledSimpleSelect
          v-model="signupMode"
          :items="[
            { text: $bct('Email'), value: 'Email' },
            { text: $bct('Cell phone'), value: 'Cell phone' },
          ]"
          :message="'Email or phone'"
          class="mb-2"
          cols="12"
          data-cy="signup-mode-selector"
          data-testid="signup-mode-selector"
          mandatory
          outlined
        />
      </template>

      <LabeledTextfield
        v-show="signupMode == 'Email'"
        v-model="email"
        @keyup.enter="draftSubmit"
        :message="'Email'"
        aria-label="Enter email"
        class="mb-2"
        cols="12"
        data-cy="input_email"
        data-testid="input_email"
        mandatory
        outlined
      />

      <LabeledTextfield
        v-show="signupMode == 'Email'"
        v-model="confirmEmail"
        @keyup.enter="draftSubmit"
        @paste.prevent
        :message="'Re-enter email'"
        aria-label="Re-enter email"
        class="mb-2"
        cols="12"
        data-cy="input_email_confirm"
        data-testid="input_email_confirm"
        mandatory
        outlined
      />

      <LabeledTextfield
        v-show="signupMode == 'Cell phone'"
        v-model="phone"
        @keyup.enter="draftSubmit"
        :message="'Cell phone number'"
        aria-label="Enter phone number"
        class="mb-2"
        cols="12"
        data-cy="input_phone"
        data-testid="input_phone"
        mask="(###) ###-####"
        placeholder="(555) 555-5555"
        hide-details
        mandatory
        outlined
      />

      <LabeledTextfield
        v-show="signupMode == 'Cell phone'"
        v-model="confirmPhone"
        @keyup.enter="draftSubmit"
        @paste.prevent
        :message="'Re-enter phone number'"
        aria-label="Re-enter phone number"
        class="mb-2"
        cols="12"
        data-cy="input_phone_confirm"
        data-testid="input_phone_confirm"
        mask="(###) ###-####"
        placeholder="(555) 555-5555"
        hide-details
        mandatory
        outlined
      />

      <template v-if="$store.state.config.enable_password_authentication">
        <LabeledTextfield
          v-model="password"
          @keyup.enter="draftSubmit"
          :message="'Password'"
          aria-label="Create password"
          class="mb-2"
          data-cy="input_password"
          data-testid="input_password"
          type="password"
          hide-details
          mandatory
          outlined
        />

        <LabeledTextfield
          v-model="passwordConfirmation"
          @keyup.enter="draftSubmit"
          :message="'Confirm password'"
          aria-label="Confirm password"
          class="mb-2"
          data-cy="input_password_confirm"
          data-testid="input_password_confirm"
          type="password"
          hide-details
          mandatory
          outlined
        />

        <v-col cols="12">
          <p class="fs-15 fw-500 mb-2">
            {{
              $bct(
                'The password must be 8 characters with 3 out of 4 of the following: Lowercase characters, Uppercase characters, Numbers, Symbols.',
              )
            }}
          </p>
        </v-col>
      </template>
    </v-row>

    <v-row
      v-show="signupMode == 'Cell phone'"
      dense
    >
      <v-col>
        <v-checkbox
          v-model="textOptIn"
          :label="
            $bct(
              'I consent to receive communication by text message to the cellphone number provided above.',
            )
          "
          data-cy="text-opt-in"
          data-testid="text-opt-in"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col
        class="c-light-black fs-15"
        tabindex="0"
      >
        {{ $bct('This site is protected by reCAPTCHA and the Google') }}
        <a href="https://policies.google.com/privacy"> {{ $bct('Privacy Policy') }}</a>
        {{ $bct('and') }}
        <a href="https://policies.google.com/terms"> {{ $bct('Terms of Service') }}</a>
        {{ $bct('apply.') }}
      </v-col>
    </v-row>

    <v-row class="mt-3">
      <v-col>
        <v-btn
          @click="draftSubmit"
          :disabled="submitDisabled"
          :loading="processing"
          :ripple="false"
          class="mnw-300 focus-very-visible"
          color="primary"
          data-cy="signup-button"
          data-testid="signup-button"
          size="x-large"
        >
          {{ $bct('Sign up') }}
        </v-btn>
      </v-col>
    </v-row>

    <template v-if="$store.state.config.terms_agreement">
      <ResourceDialog
        @save="submit"
        ref="termsAgreementDialog"
        :max-width="800"
        :processing="processing"
        :save-button-text="$store.state.config.terms_agreement.accept_label"
        :title="$store.state.config.terms_agreement.title"
        center-title
      >
        <template #form>
          <v-row>
            <v-col>
              <MarkdownContent
                :content="$store.state.config.terms_agreement?.content"
                class="focus-very-visible fs-16"
                tabindex="0"
              />
            </v-col>
          </v-row>
        </template>
      </ResourceDialog>
    </template>
  </div>
</template>

<script setup>
import { getRecaptchaEnterpriseToken } from '@/shared/services/google_recaptcha_enterprise';
import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import { useI18n } from 'vue-i18n';
import usePasswordValidation from '@/shared/composables/usePasswordValidation';
import { useRoute, useRouter } from 'vue-router';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import { useStore } from 'vuex';

const { api } = useApi();
const eventBus = useEventBus();
const { locale } = useI18n();
const { isValidPassword } = usePasswordValidation();
const route = useRoute();
const router = useRouter();
const { updateQuery } = useRouterHelper();
const store = useStore();
const $bct = inject('translator');

const confirmEmail = ref(null);
const confirmPhone = ref(null);
const email = ref(null);
const firstName = ref(null);
const lastName = ref(null);
const loaded = ref(false);
const password = ref(null);
const passwordConfirmation = ref(null);
const phone = ref(null);
const processing = ref(null);
const signupMode = ref('Email');
const termsAgreementDialog = ref(null);
const textOptIn = ref(false);

const submitDisabled = computed(() => {
  if (signupMode.value === 'Email' && (!email.value || !confirmEmail.value)) return true;
  if (signupMode.value === 'Cell phone' && (!phone.value || !textOptIn.value)) return true;
  if (store.state.config.enable_password_authentication) {
    return (
      !firstName.value ||
      !lastName.value ||
      !isValidPassword(password.value, passwordConfirmation.value)
    );
  }
  return !firstName.value || !lastName.value;
});

onMounted(load);

function load() {
  if (store.state.config.disable_self_registration) return router.replace({ name: '/' });
  if (store.state.profile) {
    loaded.value = true;
    return true;
  }

  const parent = {
    default_locale: locale.value,
    is_anonymous: true,
    is_parent: true,
    organization_id: window.organization_id,
  };
  api.member.create(parent, (resp) => {
    store.commit('setProfile', resp.data);
    loaded.value = true;
  });
  return true;
}

function createAccount(token = null) {
  if (signupMode.value === 'Cell phone') {
    email.value = null;
  } else {
    phone.value = null;
  }

  let referralRedirectUrl = null;
  if (route.query.action === 'providerReferral') {
    referralRedirectUrl = `/provider_referral?subsidyProgramId=${route.query.subsidyProgramId}&programId=${route.query.programId}`;
  }

  const params = {
    email: email.value,
    first_name: firstName.value,
    is_anonymous: false,
    is_parent: true,
    last_name: lastName.value,
    password: password.value,
    phone: phone.value,
    redirect_url: referralRedirectUrl || route.query.redirect_url,
    text_opted_in: signupMode.value === 'Cell phone',
    terms_agreement_id: store.state.config.terms_agreement?.id,
    token,
  };

  if (route.query.searchId) {
    params.redirect_url = `/searches/${route.query.searchId}`;
  }

  api.member.profile.update(
    params,
    (resp) => {
      if (resp.data.code_id) {
        const query = {
          action: route.query.action,
          mode: 'register',
          subsidyProgramId: route.query.subsidyProgramId,
          familySubsidyProgramId: route.query.familySubsidyProgramId,
          programId: route.query.programId,
          token: resp.data.code_id,
          email_sent_to: email.value,
          phone: phone.value,
          page: route.query.page,
          searchId: route.query.searchId,
        };
        router.push({ name: 'CodeSent', query });
      } else {
        store.dispatch('identify', {
          success() {
            if (route.query.action) {
              if (route.query.action === 'apply') {
                router.replace({
                  name: 'ChildSubsidyNew',
                  params: { subsidyProgramId: route.query.subsidyProgramId },
                });
              } else if (route.query.action === 'childNew') {
                router.replace({ name: 'ChildNew' });
              } else if (route.query.action === 'providerReferral') {
                router.replace({
                  name: 'ChildSubsidyNew',
                  params: { subsidyProgramId: route.query.subsidyProgramId },
                  query: { programId: route.query.programId },
                });
              } else if (route.query.action === 'familyApply') {
                router.replace({
                  name: 'FamilySubsidyNew',
                  params: { familySubsidyProgramId: route.query.familySubsidyProgramId },
                });
              } else if (route.query.searchId) {
                router.replace({
                  name: 'SearchShow',
                  params: {
                    searchId: route.query.searchId,
                  },
                  query: {
                    action: route.query.action,
                    providerId: route.query.providerId,
                  },
                });
              } else if (route.query.providerId) {
                router.replace({
                  name: 'PublicProviderShow',
                  params: {
                    providerId: route.query.providerId,
                  },
                  query: {
                    action: route.query.action,
                  },
                });
              } else {
                router.replace({ name: 'Dashboard' });
              }
            } else {
              router.replace({ name: 'Dashboard' });
            }
          },
          failure(err) {
            processing.value = false;
            eventBus.error(err);
          },
        });
      }
    },
    (err) => {
      processing.value = false;
      const error = err.response.data.errors[0];
      if (error === 'already taken') {
        if (store.state.config.enable_password_authentication) {
          eventBus.longChime(
            'Email already taken. Please login with your email and password to access your account.',
          );
        } else if (store.state.config.enable_phone_registration) {
          eventBus.longChime(
            'Email or phone is already taken. Please login with your email or phone number to access your account.',
          );
        } else {
          eventBus.longChime(
            'Email or phone is already taken. Please login with your email to access your account.',
          );
        }
      } else {
        eventBus.chime(error);
      }
    },
  );
}

async function draftSubmit() {
  if (submitDisabled.value) return false;
  if (!validate()) return false;
  if (store.state.config.terms_agreement) {
    return termsAgreementDialog.value.open();
  }
  await submit();
  return true;
}

async function submit() {
  if (submitDisabled.value) return false;

  processing.value = true;

  const token = await getRecaptchaEnterpriseToken();

  createAccount(token);

  return true;
}

function validate() {
  let error = null;
  if (signupMode.value === 'Email' && email.value !== confirmEmail.value) {
    error = 'Email and email confirmation must match';
  }
  if (signupMode.value === 'Cell phone' && phone.value !== confirmPhone.value) {
    error = 'Phone and phone confirmation must match';
  }
  if (error) {
    eventBus.chime(error);
    return false;
  }
  return true;
}
</script>
